import type { 
  LoginRequest, 
  LoginResponse, 
  User, 
  CambiarPasswordRequest
} from '../types';
import { UAU_CONFIG } from '../config/uauConfig';

// Enum para estados de sesión (compatible con el anterior)
export enum SessionState {
  VALID = 'valid',
  EXPIRED = 'expired',
  NETWORK_ERROR = 'network_error',
  INVALID = 'invalid',
  CHECKING = 'checking'
}



interface UAUPermissionsResponse {
  application_slug: string;
  user_permissions: Record<string, boolean>;
}

interface UAUUserInfo {
  id: number;
  username: string;
  email?: string;
  full_name?: string;
  is_active: boolean;
}

export class AuthServiceUAU {
  private static readonly APPLICATION_SLUG = UAU_CONFIG.APPLICATION_SLUG;
  private static readonly TOKEN_KEY = UAU_CONFIG.STORAGE_KEYS.TOKEN; // Token para MAVA
  private static readonly REFRESH_TOKEN_KEY = UAU_CONFIG.STORAGE_KEYS.REFRESH_TOKEN;
  private static readonly USER_KEY = UAU_CONFIG.STORAGE_KEYS.USER;
  private static readonly PERMISSIONS_KEY = UAU_CONFIG.STORAGE_KEYS.PERMISSIONS;

  static async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      // 1. Login contra UAU usando el método original que funcionaba
      const loginResponse = await fetch(UAU_CONFIG.ENDPOINTS.LOGIN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          username: credentials.username,
          password: credentials.password
        })
      });

      if (!loginResponse.ok) {
        const errorData = await loginResponse.json().catch(() => ({}));
        
        if (loginResponse.status === 422) {
          throw new Error('Credenciales inválidas o datos de login incorrectos');
        }
        
        throw new Error(errorData.detail || errorData.message || `Error de login (${loginResponse.status})`);
      }

      const loginData = await loginResponse.json();
      
      // 2. Extraer datos del response de UAU
      const {
        access_token,
        user_id,
        username,
        email,
        permissions,
      } = loginData;
      
      if (!access_token) {
        throw new Error('No se recibió access_token del login UAU');
      }

      // 3. Guardar solo con las claves que MAVA espera
      this.setTokenPrivate(access_token);

      // 5. Extraer permisos de MAVA del response
      const mavaPermissions = permissions?.mava || {};

      // 6. Aplanar permisos para el formato que espera MAVA
      const flatPermissions: Record<string, boolean> = {};
      Object.entries(mavaPermissions).forEach(([section, sectionPerms]: [string, any]) => {
        Object.entries(sectionPerms).forEach(([action, hasPermission]: [string, any]) => {
          flatPermissions[`${section}.${action}`] = hasPermission;
        });
      });

      // 7. Mapear respuesta al formato MAVA
      const mappedUser: User = {
        id: user_id || 0,
        username: username,
        email: email || '',
        nombre_completo: username, // UAU no devuelve full_name en este endpoint
        es_activo: true,
        tipo_usuario: this.determineUserType(flatPermissions),
        auth_service_id: (user_id || 0).toString(),
        permisos: this.convertUAUPermissionsToMava(flatPermissions),
        maquinas_asignadas: [],
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const response: LoginResponse = {
        access_token: access_token,
        refresh_token: '', // UAU no devuelve refresh token en este response
        user: mappedUser
      };

      // 8. Guardar datos de MAVA
      this.setUser(mappedUser);
      this.setPermissions(flatPermissions);
      
      return response;
    } catch (error: any) {
      throw new Error(error.message || 'Error de autenticación');
    }
  }

  private static determineUserType(uauPermissions: Record<string, boolean>): 'operador' | 'supervisor' | 'administrador' {
    // Si tiene permisos de gestión de empresas, es administrador
    if (uauPermissions['empresas.crear'] || uauPermissions['empresas.editar']) {
      return 'administrador';
    }
    
    // Si tiene permisos de aprobar reportes, es supervisor
    if (uauPermissions['reportes.aprobar']) {
      return 'supervisor';
    }
    
    // Por defecto, operador
    return 'operador';
  }

  private static convertUAUPermissionsToMava(uauPermissions: Record<string, boolean>): any[] {
    const mavaPermissions: any[] = [];
    
    Object.entries(uauPermissions).forEach(([permission, hasPermission]) => {
      if (hasPermission) {
        mavaPermissions.push({
          id: Math.random(),
          codigo: permission,
          nombre: permission,
          descripcion: `Permiso ${permission}`,
          categoria: permission.split('.')[0]
        });
      }
    });

    return mavaPermissions;
  }

  static hasPermission(mavaPermissionKey: string): boolean {
    const uauPermissions = this.getUAUPermissions();
    
    // Verificar directamente el permiso (mapeo 1:1)
    return uauPermissions[mavaPermissionKey] || false;
  }

  static hasAnyPermission(permissionKeys: string[]): boolean {
    return permissionKeys.some(permission => this.hasPermission(permission));
  }

  static hasAllPermissions(permissionKeys: string[]): boolean {
    return permissionKeys.every(permission => this.hasPermission(permission));
  }

  static async logout(): Promise<void> {
    try {
      const token = this.getToken();
      if (token) {
        await fetch(UAU_CONFIG.ENDPOINTS.LOGOUT, {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            ...UAU_CONFIG.DEFAULT_HEADERS
          }
        }).catch(() => {});
      }
    } finally {
      this.clearStorage();
    }
  }

  static async getProfile(): Promise<User> {
    const token = this.getToken();
    if (!token) throw new Error('No hay token de acceso');

    const response = await fetch(UAU_CONFIG.ENDPOINTS.ME, {
      headers: { 
        'Authorization': `Bearer ${token}`,
        ...UAU_CONFIG.DEFAULT_HEADERS
      }
    });

    if (!response.ok) throw new Error('Error al obtener perfil');

    const userInfo: UAUUserInfo = await response.json();
    await this.refreshPermissions();

    const mappedUser: User = {
      id: userInfo.id,
      username: userInfo.username,
      email: userInfo.email || '',
      nombre_completo: userInfo.full_name || userInfo.username,
      es_activo: userInfo.is_active,
      tipo_usuario: this.determineUserType(this.getUAUPermissions()),
      auth_service_id: userInfo.id.toString(),
      permisos: this.convertUAUPermissionsToMava(this.getUAUPermissions()),
      maquinas_asignadas: [],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    this.setUser(mappedUser);
    return mappedUser;
  }

  private static async refreshPermissions(): Promise<void> {
    try {
      const token = this.getToken();
      if (!token) return;

      const response = await fetch(UAU_CONFIG.ENDPOINTS.PERMISSIONS_CHECK, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          ...UAU_CONFIG.DEFAULT_HEADERS
        },
        body: JSON.stringify({ application_slug: this.APPLICATION_SLUG })
      });

      if (response.ok) {
        const permissionsData: UAUPermissionsResponse = await response.json();
        this.setPermissions(permissionsData.user_permissions);
      }
    } catch (error) {
      // Silent fail
    }
  }

  // Storage methods
  static setToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  private static setTokenPrivate(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  static getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  static setRefreshToken(token: string): void {
    localStorage.setItem(this.REFRESH_TOKEN_KEY, token);
  }

  static getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  private static setUser(user: User): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  static getUser(): User | null {
    try {
      const userData = localStorage.getItem(this.USER_KEY);
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      return null;
    }
  }

  private static setPermissions(permissions: Record<string, boolean>): void {
    localStorage.setItem(this.PERMISSIONS_KEY, JSON.stringify(permissions));
  }

  static getUAUPermissions(): Record<string, boolean> {
    try {
      const permissionsData = localStorage.getItem(this.PERMISSIONS_KEY);
      return permissionsData ? JSON.parse(permissionsData) : {};
    } catch (error) {
      return {};
    }
  }

  private static clearStorage(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.PERMISSIONS_KEY);
    localStorage.removeItem('mava_empresa_activa');
    sessionStorage.removeItem('mava_empresa_temporal_id');
    localStorage.removeItem('uau_login_data');
  }

  static isAuthenticated(): boolean {
    const token = this.getToken();
    const user = this.getUser();
    return !!(token && user);
  }

  static isAdmin(): boolean {
    const user = this.getUser();
    return user?.tipo_usuario === 'administrador' || false;
  }

  static getCurrentUserInfo(): {
    user: User | null;
    isAuthenticated: boolean;
    isAdmin: boolean;
    isSuperuser: boolean;
    permissions: string[];
    machineIds: number[];
  } {
    const user = this.getUser();
    const isAuthenticated = this.isAuthenticated();
    const isAdmin = this.isAdmin();
    
    // Por ahora, consideramos superuser igual a admin hasta tener el campo real
    const isSuperuser = isAdmin;
    
    const uauPermissions = this.getUAUPermissions();
    const permissions = Object.keys(uauPermissions).filter(key => uauPermissions[key]);
    const machineIds: number[] = [];

    return { user, isAuthenticated, isAdmin, isSuperuser, permissions, machineIds };
  }

  // Legacy compatibility methods
  static async updateProfile(data: Partial<User>): Promise<User> {
    const currentUser = this.getUser();
    if (!currentUser) throw new Error('No hay usuario autenticado');

    const updatedUser = { ...currentUser, ...data };
    this.setUser(updatedUser);
    return updatedUser;
  }

  static async changePassword(data: CambiarPasswordRequest & { password_confirmar?: string }): Promise<{ success: boolean; message: string; requires_relogin: boolean }> {
    const token = this.getToken();
    if (!token) throw new Error('No hay token de acceso');

    const response = await fetch('/mava/api/v1/auth/change-password', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        current_password: data.password_actual,
        new_password: data.password_nuevo,
        confirm_password: data.password_confirmar || data.password_nuevo
      })
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.detail || 'Error al cambiar la contraseña');
    }

    return result;
  }

  static getUserMachines(): number[] {
    const user = this.getUser();
    return user?.maquinas_asignadas?.map(ma => ma.maquina_id) || [];
  }

  static hasAccessToMachine(machineId: number): boolean {
    if (this.isAdmin()) return true;
    const userMachines = this.getUserMachines();
    return userMachines.includes(machineId);
  }

  static clearStorageSilently(): void {
    this.clearStorage();
  }

  // Session monitoring methods for compatibility
  static handleSessionExpired(): void {
    this.clearStorageSilently();
    
    // Guardar información de contexto antes de redirigir
    const currentPath = window.location.pathname;
    if (currentPath !== '/login') {
      sessionStorage.setItem('mava_redirect_after_login', currentPath);
    }
    
    // Redirección con mensaje
    const loginUrl = '/login?expired=true';
    setTimeout(() => {
      window.location.href = loginUrl;
    }, 100);
  }

  static setSessionExpiredCallback(_callback: () => void): void {
    // No-op for UAU (handled by API errors)
  }

  static redirectToLogin(): void {
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }

  static redirectToLoginWithMessage(): void {
    this.redirectToLogin();
  }

  static async checkSessionState(): Promise<SessionState> {
    try {
      const token = this.getToken();
      if (!token) {
        return SessionState.INVALID;
      }

      // Verificar con UAU usando /me endpoint
      const response = await fetch(UAU_CONFIG.ENDPOINTS.ME, {
        headers: {
          'Authorization': `Bearer ${token}`,
          ...UAU_CONFIG.DEFAULT_HEADERS
        }
      });

      if (response.ok) {
        return SessionState.VALID;
      } else if (response.status === 401) {
        return SessionState.EXPIRED;
      } else {
        return SessionState.NETWORK_ERROR;
      }
    } catch (error) {
      return SessionState.NETWORK_ERROR;
    }
  }
}
